export class EpisodeList {
    constructor(container, seriesId, episodes, seriesType = 'webtoon') {
        this.container = container;
        this.seriesId = seriesId;
        this.episodes = episodes;
        this.viewMode = seriesType === 'webnovel' ? 'list' : 'grid';
        this.sortOrder = 'asc';
        this.filter = 'all';
        this.readHistory = {};
        this.purchasedMap = {}; // [NEW] Ownership Data
        this.itemsPerPage = 50;
        this.currentPage = 1;
    }

    setHistory(history, purchasedMap = {}) {
        this.readHistory = history || {};
        this.purchasedMap = purchasedMap || {};

        if (this.container.querySelector('#ep-list-container')) {
            this.renderList();
            this.updateContinueButton();
        }
    }

    // ... (keep updateContinueButton as is)

    // Helper for Badge
    getBadge(ep) {
        if (!this.purchasedMap) return '';
        const info = this.purchasedMap[ep.id];
        if (!info) return '';

        if (info.type === 'own') {
            return '<span class="px-1.5 py-0.5 bg-purple-500/20 text-purple-300 text-[10px] font-bold rounded border border-purple-500/30 ml-2">소장</span>';
        } else if (info.type === 'rent') {
            const now = new Date();
            const exp = info.expiresAt?.toDate ? info.expiresAt.toDate() : new Date(info.expiresAt);
            const diff = exp - now;
            if (diff > 0) {
                const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
                return `<span class="px-1.5 py-0.5 bg-green-500/20 text-green-300 text-[10px] font-bold rounded border border-green-500/30 ml-2">대여 D-${days}</span>`;
            }
        }
        return '';
    }

    render() {
        // [FIX] Restore Controls (Sort, View Mode)
        this.container.innerHTML = `
            <div class="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <div class="flex items-center gap-2">
                    <h2 class="text-xl font-bold text-white flex items-center gap-2">
                        Episodes <span class="text-zinc-500 text-sm font-normal">(${this.episodes.length})</span>
                    </h2>
                </div>

                <div class="flex items-center gap-3">
                    <!-- Read First Button -->
                    <button id="btn-read-first" class="px-3 py-1.5 rounded-full border border-zinc-700 bg-zinc-800/50 text-xs font-bold text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors flex items-center gap-1">
                         <span class="material-symbols-outlined text-sm">play_arrow</span> 첫화보기
                    </button>

                    <div class="h-4 w-px bg-zinc-700 mx-1"></div>

                    <!-- Sort -->
                    <button id="btn-sort" class="flex items-center gap-1 text-zinc-400 hover:text-white text-xs font-bold transition-colors">
                        <span class="material-symbols-outlined text-sm">swap_vert</span>
                        <span id="sort-label">${this.sortOrder === 'asc' ? '첫화부터' : '최신순'}</span>
                    </button>

                    <!-- View Mode -->
                    <div class="flex bg-zinc-800 rounded-lg p-0.5 border border-zinc-700">
                        <button id="btn-view-list" class="p-1 px-2 rounded-md ${this.viewMode === 'list' ? 'bg-zinc-600 text-white shadow-sm' : 'text-zinc-400 hover:text-white'} transition-all">
                            <span class="material-symbols-outlined text-sm block">format_list_bulleted</span>
                        </button>
                        <button id="btn-view-grid" class="p-1 px-2 rounded-md ${this.viewMode === 'grid' ? 'bg-zinc-600 text-white shadow-sm' : 'text-zinc-400 hover:text-white'} transition-all">
                            <span class="material-symbols-outlined text-sm block">grid_view</span>
                        </button>
                    </div>
                </div>
            </div>

            <div id="ep-list-container"></div>
            <div id="pagination-controls" class="flex justify-center gap-2 mt-8 pb-10"></div>
        `;

        this.bindHeaderEvents();
        this.renderList();
    }

    bindHeaderEvents() {
        // Read First
        const firstBtn = this.container.querySelector('#btn-read-first');
        if (firstBtn) {
            firstBtn.onclick = () => {
                if (this.episodes.length > 0) {
                    // Assuming episodes sorted by index? logic usually: find index 1 or smallest
                    // Standard: just grab the one with lowest index
                    const first = [...this.episodes].sort((a, b) => a.index - b.index)[0];
                    if (first) window.router.navigate(`/series/${this.seriesId}/episode/${first.id}`);
                }
            };
        }

        // Sort
        const sortBtn = this.container.querySelector('#btn-sort');
        const sortLabel = this.container.querySelector('#sort-label');
        if (sortBtn) {
            sortBtn.onclick = () => {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
                sortLabel.textContent = this.sortOrder === 'asc' ? '첫화부터' : '최신순';
                this.currentPage = 1; // Reset page
                this.renderList();
            };
        }

        // View Mode
        const btnList = this.container.querySelector('#btn-view-list');
        const btnGrid = this.container.querySelector('#btn-view-grid');

        const updateViewBtns = () => {
            if (this.viewMode === 'list') {
                btnList.className = 'p-1 px-2 rounded-md bg-zinc-600 text-white shadow-sm transition-all';
                btnGrid.className = 'p-1 px-2 rounded-md text-zinc-400 hover:text-white transition-all';
            } else {
                btnList.className = 'p-1 px-2 rounded-md text-zinc-400 hover:text-white transition-all';
                btnGrid.className = 'p-1 px-2 rounded-md bg-zinc-600 text-white shadow-sm transition-all';
            }
        };

        if (btnList && btnGrid) {
            btnList.onclick = () => {
                this.viewMode = 'list';
                updateViewBtns();
                this.renderList();
            };
            btnGrid.onclick = () => {
                this.viewMode = 'grid';
                updateViewBtns();
                this.renderList();
            };
        }
    }
    renderList() {
        // ... (sorting and pagination logic same) ...
        const sorted = [...this.episodes].sort((a, b) => {
            return this.sortOrder === 'asc' ? a.index - b.index : b.index - a.index;
        });

        // Pagination Slice
        const totalItems = sorted.length;
        const totalPages = Math.ceil(totalItems / this.itemsPerPage);

        // Safety check
        if (this.currentPage > totalPages) this.currentPage = totalPages || 1;
        if (this.currentPage < 1) this.currentPage = 1;

        const startIdx = (this.currentPage - 1) * this.itemsPerPage;
        const endIdx = startIdx + this.itemsPerPage;
        const pageItems = sorted.slice(startIdx, endIdx);

        // Determine Last Read Index for "Watched Upto" logic
        let lastReadIndex = -1;
        if (this.readHistory && this.readHistory.lastEpisodeId) {
            // Find the index of the last read episode
            const lastEp = this.episodes.find(ep => String(ep.id) === String(this.readHistory.lastEpisodeId));
            if (lastEp) lastReadIndex = lastEp.index;
        }

        const container = this.container.querySelector('#ep-list-container');
        container.innerHTML = '';

        if (this.viewMode === 'grid') {
            // GRID VIEW
            container.className = "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4";
            pageItems.forEach(ep => {
                const isRead = this.isRead(ep.id, ep.index, lastReadIndex);
                const badge = this.getBadge(ep);

                const el = document.createElement('div');
                el.className = `group relative aspect-video bg-zinc-900 rounded-lg overflow-hidden border ${isRead ? 'border-zinc-700 opacity-60' : 'border-white/5'} hover:border-white/20 hover:scale-[1.02] hover:opacity-100 transition-all cursor-pointer`;

                let thumb = ep.thumbnail;
                if (thumb && (thumb.includes('pstatic') || thumb.includes('naver'))) thumb = "https://wsrv.nl/?url=" + encodeURIComponent(thumb);

                el.innerHTML = `
                    <img src="${thumb}" class="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" loading="lazy">
                    <div class="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent p-3 flex flex-col justify-end">
                        <div class="flex justify-between items-end">
                            <div class="w-full">
                                <div class="flex justify-between items-center mb-0.5">
                                     <span class="text-[10px] font-bold ${isRead ? 'text-zinc-400' : 'text-indigo-400'}">EP ${ep.index}</span>
                                     ${badge}
                                </div>
                                <h3 class="text-xs md:text-sm font-bold text-white line-clamp-1 group-hover:line-clamp-none transition-all">${ep.title}</h3>
                            </div>
                        </div>
                    </div>
                    ${ep.status === 'draft' ? '<div class="absolute top-2 right-2"><span class="text-[10px] font-bold text-red-400 bg-black/50 px-1.5 py-0.5 rounded border border-red-500/30">Draft</span></div>' : ''}
                    ${isRead ? '<div class="absolute top-2 left-2"><span class="material-symbols-outlined text-zinc-500 text-sm bg-black/50 rounded-full p-0.5">check</span></div>' : ''}
                `;
                el.onclick = () => window.router.navigate(`/series/${this.seriesId}/episode/${ep.id}`);
                container.appendChild(el);
            });

        } else {
            // LIST VIEW
            container.className = "space-y-2";
            pageItems.forEach(ep => {
                const isRead = this.isRead(ep.id, ep.index, lastReadIndex);
                const badge = this.getBadge(ep);

                const el = document.createElement('div');
                el.className = `flex items-center gap-3 p-3 rounded-lg bg-zinc-900/50 border ${isRead ? 'border-zinc-800 opacity-60' : 'border-white/5'} hover:bg-zinc-800 hover:opacity-100 transition-all cursor-pointer group`;

                let thumb = ep.thumbnail;
                if (thumb && (thumb.includes('pstatic') || thumb.includes('naver'))) thumb = "https://wsrv.nl/?url=" + encodeURIComponent(thumb);

                el.innerHTML = `
                    <div class="w-16 h-12 flex-shrink-0 bg-black rounded overflow-hidden relative">
                        <img src="${thumb}" class="w-full h-full object-cover opacity-70 group-hover:opacity-100">
                        ${isRead ? '<div class="absolute inset-0 bg-black/60 flex items-center justify-center"><span class="material-symbols-outlined text-zinc-400 text-lg">check</span></div>' : ''}
                    </div>
                    <div class="flex-grow min-w-0">
                        <div class="flex items-center gap-2">
                            <span class="text-[10px] font-bold ${isRead ? 'text-zinc-500' : 'text-indigo-400'}">Episode ${ep.index}</span>
                            ${badge}
                            ${ep.status === 'draft' ? '<span class="text-[10px] font-bold text-red-400 bg-red-900/30 px-1.5 py-0.5 rounded border border-red-500/30">Draft</span>' : ''}
                        </div>
                        <h3 class="text-sm font-bold ${isRead ? 'text-zinc-400' : 'text-white'} truncate group-hover:text-indigo-300 transition-colors">${ep.title}</h3>
                    </div>
                    <div class="flex-shrink-0">
                         <span class="material-symbols-outlined text-zinc-600 group-hover:text-white transition-colors">chevron_right</span>
                    </div>
                `;
                el.onclick = () => window.router.navigate(`/series/${this.seriesId}/episode/${ep.id}`);
                container.appendChild(el);
            });
        }

        this.renderPagination(totalPages);
    }

    renderPagination(totalPages) {
        const container = this.container.querySelector('#pagination-controls');
        if (totalPages <= 1) {
            container.innerHTML = '';
            return;
        }

        container.innerHTML = ''; // Clear

        // Helper to create btn
        const createBtn = (text, page, isActive = false, isDisabled = false) => {
            const btn = document.createElement('button');
            btn.className = `min-w-[32px] h-8 rounded-lg px-2 flex items-center justify-center text-sm font-bold transition-colors ${isActive ? 'bg-indigo-600 text-white' :
                isDisabled ? 'border border-zinc-800 text-zinc-600 cursor-not-allowed' : 'border border-zinc-700 text-zinc-400 hover:bg-zinc-800'
                }`;
            btn.innerHTML = text;
            if (isActive || isDisabled) {
                btn.disabled = true;
            } else {
                btn.onclick = () => {
                    this.currentPage = page;
                    this.renderList();
                    this.container.querySelector('#ep-list-container')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                };
            }
            return btn;
        };

        // Prev
        container.appendChild(createBtn('<span class="material-symbols-outlined text-sm">chevron_left</span>', this.currentPage - 1, false, this.currentPage === 1));

        // Pages
        let pages = [];
        if (totalPages <= 7) {
            pages = Array.from({ length: totalPages }, (_, i) => i + 1);
        } else {
            if (this.currentPage <= 4) {
                pages = [1, 2, 3, 4, 5, '...', totalPages];
            } else if (this.currentPage >= totalPages - 3) {
                pages = [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
            } else {
                pages = [1, '...', this.currentPage - 1, this.currentPage, this.currentPage + 1, '...', totalPages];
            }
        }

        pages.forEach(p => {
            if (p === '...') {
                const dots = document.createElement('span');
                dots.className = "text-zinc-500 px-1";
                dots.textContent = "...";
                container.appendChild(dots);
            } else {
                container.appendChild(createBtn(String(p), p, p === this.currentPage));
            }
        });

        // Next
        container.appendChild(createBtn('<span class="material-symbols-outlined text-sm">chevron_right</span>', this.currentPage + 1, false, this.currentPage === totalPages));
    }

    isRead(epId, epIndex, lastReadIdx) {
        // If exact match (old logic)
        if (this.readHistory && String(this.readHistory.lastEpisodeId) === String(epId)) return true;

        // If we have a valid lastReadIndex, mark everything before it as read
        if (lastReadIdx !== -1 && epIndex !== undefined && epIndex <= lastReadIdx) return true;

        return false;
    }
}

